<script setup lang="ts">
import { reactive, ref } from 'vue';
import CustomStore from 'devextreme/data/custom_store';
import type { LoadOptions } from 'devextreme/data';
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxForm, DxSimpleItem, DxRequiredRule } from 'devextreme-vue/form';
import { bottoGridPaging, showError, showSuccess } from '@botto/core';
import { groupsApi, type TrackerGroup } from '../../api';
import { formatDateTime } from '../../utils/format';
import { pageFromLoadOptions, resetGridPage, trackerGridProps } from '../../utils/gridUi';

const { paging, pager } = bottoGridPaging({ pageSize: 50 });
const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);

const showModal = ref(false);
const editing = ref<TrackerGroup | null>(null);
const saving = ref(false);
const formError = ref('');
const form = reactive({ name: '' });

const store = new CustomStore({
  key: 'id',
  loadMode: 'processed',
  async load(loadOptions: LoadOptions) {
    const { page, page_size } = pageFromLoadOptions(loadOptions, paging.pageSize);
    try {
      const res = await groupsApi.listGroups({ page, page_size });
      return { data: res.items || [], totalCount: res.total || 0 };
    } catch (e) {
      showError(e);
      return { data: [], totalCount: 0 };
    }
  },
});

function refreshGrid() {
  resetGridPage(grid.value?.instance ?? null);
}

async function refresh() {
  try {
    await grid.value?.instance?.refresh();
  } catch (e) {
    showError(e);
  }
}

function openCreate() {
  editing.value = null;
  form.name = '';
  formError.value = '';
  showModal.value = true;
}

function openEdit(g: TrackerGroup) {
  editing.value = g;
  form.name = g.name;
  formError.value = '';
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

async function save() {
  saving.value = true;
  formError.value = '';
  try {
    const name = form.name.trim();
    if (!name) {
      formError.value = 'Имя обязательно';
      return;
    }
    if (editing.value) {
      await groupsApi.updateGroup(editing.value.id, { name });
      showSuccess('Группа обновлена');
    } else {
      await groupsApi.createGroup({ name });
      showSuccess('Группа создана');
    }
    closeModal();
    refreshGrid();
  } catch (e) {
    formError.value = e instanceof Error ? e.message : 'Ошибка сохранения';
  } finally {
    saving.value = false;
  }
}

async function removeGroup(g: TrackerGroup) {
  if (g.name === 'Default') return;
  if (!window.confirm(`Удалить группу «${g.name}»? Это нельзя отменить.`)) return;
  try {
    await groupsApi.deleteGroup(g.id);
    showSuccess('Группа удалена');
    await grid.value?.instance?.refresh();
  } catch (e) {
    showError(e);
  }
}
</script>

<template>
  <div class="groups-panel">
    <div class="groups-panel__toolbar">
      <DxButton icon="add" text="Добавить" type="default" styling-mode="contained" @click="openCreate" />
      <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
    </div>
    <p class="groups-panel__hint">
      Организация ссылок по именованным группам. Группу Default удалить нельзя.
    </p>

    <div class="groups-panel__body">
      <DxDataGrid
        ref="grid"
        :data-source="store"
        :remote-operations="{ paging: true, sorting: false, filtering: false }"
        v-bind="trackerGridProps"
        :paging="paging"
        :pager="pager"
        height="100%"
        width="100%"
      >
        <DxScrolling mode="standard" />

        <DxColumn data-field="id" caption="ID" width="80" />
        <DxColumn data-field="name" caption="Имя" />
        <DxColumn
          data-field="updated_at"
          caption="Обновлена"
          width="180"
          :calculate-cell-value="(row: TrackerGroup) => formatDateTime(row.updated_at)"
        />

        <DxColumn type="buttons" :width="100" caption="Действия">
          <DxGridButton hint="Переименовать" icon="edit" :on-click="(e: any) => openEdit(e.row.data)" />
          <DxGridButton hint="Удалить" icon="trash" :on-click="(e: any) => removeGroup(e.row.data)" />
        </DxColumn>
      </DxDataGrid>
    </div>

    <DxPopup
      v-model:visible="showModal"
      :title="editing ? 'Переименовать группу' : 'Добавить группу'"
      :width="420"
      :height="'auto'"
      :show-close-button="true"
      @hiding="closeModal"
    >
      <template #content>
        <div v-if="formError" class="form-error">{{ formError }}</div>
        <DxForm :form-data="form" label-location="top">
          <DxSimpleItem
            data-field="name"
            editor-type="dxTextBox"
            :label="{ text: 'Имя' }"
            :editor-options="{ placeholder: 'Campaign A' }"
          >
            <DxRequiredRule message="Обязательно" />
          </DxSimpleItem>
        </DxForm>
      </template>
      <DxToolbarItem widget="dxButton" toolbar="bottom" location="after" :options="{ text: 'Отмена', onClick: closeModal }" />
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: saving ? 'Сохранение…' : 'Сохранить', type: 'default', disabled: saving, onClick: save }"
      />
    </DxPopup>
  </div>
</template>

<style scoped>
.groups-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 10px;
}

.groups-panel__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.groups-panel__hint {
  margin: 0;
  font-size: 0.875rem;
  color: var(--dx-color-text-secondary, #666);
}

.groups-panel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.form-error {
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fdecea;
  color: #c62828;
  font-size: 0.875rem;
}
</style>
